/**
 * Provides an interface for viewing and modifying map-related `FormResponse` attributes and accessing/updating the files associated with a form response. If the current user is not allowed to modify the object, they will be shown a read-only version of the interface.
 *
 * ## Fields
 * Name | Description
 * --- | ---
 * header | The `PanelHeadingView` associated with this control.
 * @module app/views/map/ObjectPanelView
 */
import Backbone from "backbone";
import _ from "underscore";
import DetailView from "core/views/DetailView";
import PanelHeadingView from "core/views/PanelHeadingView";
import FormView from "core/views/FormView";
import ButtonView from "core/views/ButtonView";
import $ from "jquery";
import DirectoryContainerViewBase from "app/views/DirectoryContainerViewBase";
import TabView from "core/views/TabView";
import auth from "app/auth";
import ErrorUtils from "app/utils/ErrorUtils";
import GeographicSummarizer from "app/models/map/summarize/GeographicSummarizer";
import isMobile from "core/is-mobile";
import mapContext from "app/views/map/MapContext";
var ObjectPanelView = Backbone.View.extend(
  {
    className: "detail-view object-detail",

    /**
     * Initializes the view.
     * @param  {object} options The options hash.
     * @param {string} options.title:'Object Details' A title for the view.
     * @param {FeatureData} options.model The model to display.
     * @param {MapLayerBase} options.layer The layer associated with the model to display.
     * @param {module:app/views/map/MapContext} options.context The map context to use for this instance.
     */
    initialize: function (options) {
      /*Other options used: model, mapObject, question*/
      this.options = _.defaults({}, options, {
        title: "Object Details",
        fields: [],
      });

      this.header = new PanelHeadingView({
        title: this.options.title,
      });

      this._isInternal = !this.options.layer.serviceLayer.service.get(
        "external"
      );
    },

    _setFooter: function (content) {
      this._footer = content;
      var footer = this.$("> .panel-footer");

      if (_.isBoolean(content)) {
        if (content) {
          footer.show();
        } else {
          footer.hide();
        }
      } else {
        if (_.isString(content)) {
          footer.html(content);
        } else {
          footer.html("").append(content);
        }

        footer.append('<div class="clearfix"></div>');

        footer.show();
      }
    },

    _renderDetail: function (model, fields) {
      var view;

      view = new DetailView({
        model: model.getFeature().getProperties(),
        fields: fields,
      });

      view.render();

      return view;
    },

    _deleteButtonClick: function () {
      var self = this;

      return function () {
        return mapContext
          .getOperation("remove-geometry")
          .action([self.options.model])
          .fail(
            ErrorUtils.errorHandler({
              activity: "removing the geometry",
            })
          );
      };
    },

    _saveButtonClick: function (formView, continueDisplay) {
      var self = this;

      return function () {
        formView
          .propagate()
          .done(function () {
            return mapContext
              .getOperation("commit-geometry")
              .action([self.options.model])
              .done(function (model) {
                if (!isMobile || continueDisplay) {
                  mapContext.display(model);
                }
              });
          })
          .fail(
            ErrorUtils.errorHandler({
              activity: "saving the geometry",
            })
          );
      };
    },

    _renderForm: function (model, fields) {
      var view;

      view = new FormView({
        model: model.getFeature().getProperties(),
        fields: fields,
      });

      view.render();

      return view;
    },

    _renderFormButtons: function (formView) {
      var model = this.options.model;
      var save = "Save";
      var remove = "Delete";

      var buttons = [];
      var size = isMobile ? " btn-lg" : "";

      if (model.getFeature().get("canUpdate")) {
        var saveButton = new ButtonView({
          text: save,
          type: "primary",
          className: size,
          click: this._saveButtonClick(formView),
        });

        saveButton.render();
        buttons.push(saveButton);
      }

      if (model.getFeature().get("canDelete")) {
        var deleteButton = new ButtonView({
          text: remove,
          type: "danger",
          className: "delete-button" + size,
          click: this._deleteButtonClick(),
        });

        deleteButton.render();
        buttons.push(deleteButton);
      }

      return buttons;
    },

    _renderFiles: function () {
      var model = this.options.model;
      var canUpdate = model.getFeature().get("canUpdate");
      var fileView = new ObjectPanelView.ObjectFileView({
        objectView: this,
        initialFolder: this.options.layer.getRootFilePath(model),
        title: "Object Files",
        allowAddFolder: canUpdate,
        allowAddFiles: canUpdate,
        allowDropFiles: canUpdate,
      });

      fileView.render();

      return fileView;
    },

    _renderFileButtons: function (fileView) {
      var buttons = [];

      if (this.options.model.getFeature().get("canUpdate")) {
        var addFolder = new ButtonView({
          text: "Add Folder",
          className: isMobile ? "btn-lg" : "",
          click: _.bind(fileView.addFolderRequest, fileView),
        });
        addFolder.render();
        buttons.push(addFolder);

        if (!isMobile) {
          var addFile = new ButtonView({
            text: "Upload",
            click: _.bind(fileView.addFileRequest, fileView),
          });

          addFile.render();
          buttons.push(addFile);
        }
      }

      buttons.push(fileView.upButton);

      return buttons;
    },

    _buildTabs: function (model, fields) {
      var self = this;
      var fileView = null;
      var showFiles = !(
        (this._isInternal && model.isNew()) ||
        !(
          this.options.layer.hasFiles() &&
          this.options.layer.getRootFilePath(model)
        )
      );
      var firstTabKey = "";
      var groupedFields = _.groupBy(fields, function (field) {
        if (field.get("category") == "Details") {
          firstTabKey = "Details";
        }

        return field.get("category") || "";
      });
      var groupedFieldNames = _.keys(groupedFields).sort();
      var firstTab = groupedFields[firstTabKey] || [];

      if (firstTabKey !== "") {
        firstTab = firstTab.concat(groupedFields[""] || []);
        groupedFields[firstTabKey] = firstTab;
      }

      groupedFieldNames = [firstTabKey].concat(
        _.without(groupedFieldNames, firstTabKey, "")
      );

      if (this.options.layer.serviceLayer) {
        firstTab.unshift(
          this.options.layer.serviceLayer.getLayerField(
            model.getFeature().get("canUpdate")
          )
        );
        firstTab.unshift(this.options.layer.serviceLayer.getServiceField());
      }

      if (
        model != ObjectPanelView.currentModel ||
        ObjectPanelView.currentDetailTabIndex >= groupedFieldNames.length
      ) {
        ObjectPanelView.currentDetailTabIndex = 0;
        ObjectPanelView.currentDetailTabName = "Details";
        ObjectPanelView.currentModel = model;
      }

      var tabs = _.map(groupedFieldNames, function (fieldName, idx) {
        var tabFields = groupedFields[fieldName] || [];
        var view,
          buttons = [];
        var doSave = false;

        fieldName = fieldName || "Details";

        if (
          model.getFeature().get("canUpdate") ||
          (self._isInternal && model.isNew())
        ) {
          view = self._renderForm(model, tabFields);
          buttons = self._renderFormButtons(view);
          doSave = self._saveButtonClick(view, true);
        } else {
          view = self._renderDetail(model, tabFields);
        }

        return {
          view: view,
          buttons: buttons,
          title: fieldName,
          start: function () {
            if (fileView) {
              fileView.active = false;
            }
            self.header.setTitle(self.options.title);

            if (ObjectPanelView.currentDetailTabName !== fieldName) {
              ObjectPanelView.currentDetailTabName = fieldName;
              ObjectPanelView.currentDetailTabIndex = idx;

              if (_.isFunction(doSave)) {
                doSave();
              }
            }
          },
        };
      });

      if (showFiles) {
        fileView = this._renderFiles();

        tabs.push({
          view: fileView,
          buttons: this._renderFileButtons(fileView),
          title: "Files",
          start: function () {
            if (fileView) {
              fileView.active = true;
              fileView.navigateStart();
            }
          },
        });
      }

      var summarizers = [];

      if (!this.options.layer.serviceLayer.service.get("external")) {
        summarizers.push(new GeographicSummarizer());
      }

      _.each(
        summarizers,
        function (summarizer) {
          var geometry = this.options.model.getFeature().getGeometry();
          var summary = summarizer.summarizeGeometry(geometry);
          var detailView = new DetailView({
            model: summary,
            fields: summary.summary_fields,
          });

          detailView.listenTo(
            geometry,
            "change",
            _.debounce(
              function () {
                var summary = summarizer.summarizeGeometry(geometry);

                detailView.model = summary;
                detailView.fields = summary.summary_fields;
                detailView.render();
              },
              100,
              true
            )
          );

          detailView.render();

          tabs.push({
            view: detailView,
            buttons: [],
            title: summarizer.getName(),
            start: function () {},
          });
        },
        this
      );

      /*
		_.each(this.options.context.plugins, function (plugin) {
			if (plugin.handlesObjects([model])) {
				var view = plugin.viewForObject(model);

				if (view) {
					tabs.push({
						view: new Backbone.View({
							el: view[0]
						}),
						buttons: [],
						title: plugin.name,
						start: function () {
							fileView.active = false;
							self.header.setTitle(self.options.title);
						}
					});
				}
			}
		}, this);
		*/

      return tabs;
    },

    _renderTabs: function (tabs) {
      this.$el.uniqueId();
      var tabView = new TabView({
        initialTab: ObjectPanelView.currentDetailTabIndex,
        tabs: tabs,
        prefix: this.$el.attr("id"),
      });

      tabView.on(
        "enter",
        function (tab) {
          var buttons = $();
          var hide = true;
          _.each(tab.buttons, function (buttonView) {
            buttonView.render();
            buttonView.delegateEvents();
            if (buttonView.$el.css("display") != "none") {
              //check if a button is visible
              hide = false;
            }
            buttons = buttons.add(buttonView.$el);
          });
          tab.start();

          this._setFooter(buttons);
          //if each button is hidden, hide the footer
          if (hide) {
            this._setFooter(false);
          }
        },
        this
      );
      tabView.render();

      return tabView.$el;
    },

    render: function () {
      var model = this.options.model;
      var fields = this.options.layer.fields;

      fields = fields.slice();

      var tabs = this._buildTabs(model, fields);

      this.$el
        .html('<div class="panel-footer"></div>')
        .prepend(this._renderTabs(tabs));
    },
  },
  {
    ObjectFileView: DirectoryContainerViewBase.extend({
      initialize: function (options) {
        DirectoryContainerViewBase.prototype.initialize.apply(this, arguments);
        this.objectView = this.options.objectView;
        this.upButton = new ButtonView({
          text: "Up",
          className: isMobile ? "btn-lg" : "",
          click: _.bind(this.navigateUp, this),
        });
        this.navigateStart();
        this.active = false;
      },
      render: function () {
        this.upButton.render();
        this.doneRendering();
      },

      leftRoot: function () {
        this.upButton.$el.show();
      },

      atRoot: function () {
        this.upButton.$el.hide();
      },

      setHeading: function (heading) {
        if (this.active) {
          //this.objectView.header.setTitle(heading);
        }
      },

      setView: function (directory) {
        this.$el.children().remove();
        this.$el.append(directory.$el);
      },

      getFolderUrl: function (folderModel) {
        return this.objectView.options.layer.getChildDirectoryPath(folderModel);
      },

      getFolderName: function (folderModel) {
        return folderModel.get("Name");
      },

      fileUrl: function (fileModel) {
        return this.objectView.options.layer.getChildFilePath(fileModel);
      },

      dragNotSupported: function () {
        //this.footer = $('<div class="panel-footer">This browser does not support file drag-and-drop, but you can still add individual files with the "Upload" button.</div>');
      },
    }),

    /**
     * The name of the currently selected detail tab
     */
    currentDetailTabName: "Details",

    /**
     * The index of the currently selected detail tab
     */
    currentDetailTabIndex: 0,

    /**
     * The model currently displayed
     */
    currentModel: null,
  }
);

export default ObjectPanelView;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/map/ObjectPanelView.js
// module id = 946
// module chunks = 1